<template>
  <div
    class="side-by-side-container px-6"
    @copy="sideBySideCopyKeyboard({
      id: jobId,
      revisionId: revision.id,
      revisionColumn: true,
      type: sideBySideView,
    })"
  >
    <PageTurner
      v-if="revision && similarJobsView"
      v-bind="{ page, totalPages, pageEnds: { start: 1, end: totalPages } }"
      reverse-pages
      :style="similarJobsView ? { marginTop: '-8px' } : {}"
      @next-page="handleNextPage"
      @prev-page="handlePrevPage"
    >
      <template #title>
        <div class="similar-jobs-header">
          Similar Jobs
        </div>
      </template>
      <template #pagedContent>
        <Revision
          :revision="revision[pageIndex]"
          :job-id="jobId"
          :is-linked="isLinked"
          :style="similarJobsView ? { marginTop: '8px' } : {}"
        />
      </template>
    </PageTurner>

    <Revision
      v-if="revision && !similarJobsView"
      :revision="revision"
      :job-id="jobId"
      :is-linked="isLinked"
    >
      <template #header>
        <div class="revision-header">
          {{ $route.query.notAppliedYet === 'true' ? '&nbsp;' : 'Original Description' }}
        </div>
      </template>
    </Revision>
  </div>
</template>

<script>
import { useDatapeopleAnywhereEditor } from '@/stores/document/datapeopleAnywhereEditor';
import { mapActions, mapState } from 'pinia';
import { vueGet } from '@/api/vue';
// TODO: remove after new similar jobs implementation is complete
import { recruitGetSimilarJobsOld } from '@/api/recruit';
import { useTracking } from '@/stores/tracking/tracking';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppLoading } from '@/stores/app/loading';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useEditor } from '@/stores/editor/editor';
import { useSimilarJobs } from '@/stores/document/similarJobs';
import PageTurner from '@/components/shared/PageTurner.vue';
import Revision from '@/components/editor/Revision.vue';

export default {
  name: 'SideBySideContainer',
  components: {
    PageTurner,
    Revision,
  },
  props: {
    jobId: { type: [String, Number], default: null },
    isLinked: { type: Number, default: 0 },
  },
  data() {
    return {
      page: 1,
      revision: null,
    };
  },
  computed: {
    ...mapState(useAppAuthData, ['authData']),
    ...mapState(useEditor, ['sideBySideView']),
    ...mapState(useSimilarJobs, { similarJobsNew: 'jobs' }),
    // TODO: remove after new similar jobs implementation is complete
    ...mapState(useEditor, { similarJobsOld: 'similarJobs' }),
    similarJobs() {
      return this.authData.nextUiEnabled ? this.similarJobsNew : this.similarJobsOld;
    },
    ...mapState(useEditor, {
      stateRevisionId: (editorState) => editorState.revisionId,
    }),
    ...mapState(useDatapeopleAnywhereEditor, ['isDatapeopleAnywhereEditor']),
    ...mapState(useDatapeopleAnywhereEditor, {
      datapeopleAnywhereCurrentRevision: 'currentRevision',
      datapeopleAnywherePreviousRevision: 'previousRevision',
      datapeopleAnywhereSimilarJobId: 'similarJobId',
    }),

    similarJobsView() {
      return this.sideBySideView === 'similarJobs'
        || this.sideBySideView === 'datapeopleAnywhereSimilarJob';
    },
    pageIndex() {
      if (Number.isNaN(this.page) || this.page <= 0) {
        return 0;
      }
      return this.page - 1;
    },
    totalPages() {
      if (Array.isArray(this.similarJobs)) {
        return this.similarJobs.length;
      }
      return 1;
    },
  },
  watch: {
    stateRevisionId(revisionId, oldValue) {
      if (
        this.$route.query?.view === 'chrome'
        && this.$route.query?.sideRevision === 'chromePrefilled'
        && (revisionId && !oldValue)
      ) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            chromeFirstRevision: this.stateRevisionId,
          },
        }).catch(() => {});
      }
    },
  },
  async mounted() {
    if (this.$route.query.notAppliedYet) {
      this.addInfoSnackbar({
        text: 'The fields in these two jobs are not compatible so you will need '
          + 'to copy the relevant content from the left view to the editor on the right.',
      });
    }

    try {
      /* setting load component will trigger editor loader */
      this.setLoading({ componentLoading: true });

      if (this.similarJobsView) {
        if (this.authData.nextUiEnabled && !this.similarJobs?.length) {
          this.$router.replace({
            query: {
              ...this.$route.query,
              similarJobs: undefined,
            },
          }).catch(() => false);
          return;
        }

        // TODO: remove after new similar jobs implementation is complete
        if (!this.authData.nextUiEnabled && this.similarJobs === null) {
          const { similarJobs } = await recruitGetSimilarJobsOld({ jobId: this.jobId });
          this.setSimilarJobs(similarJobs);

          const firstTapwageJob = this.similarJobs.find((job) => job.status === null) || {};
          firstTapwageJob.isFirstTapwageJob = true;
        }

        let similarJobsId = this.$route?.query.similarJobs;
        if (this.isDatapeopleAnywhereEditor) {
          similarJobsId = this.datapeopleAnywhereSimilarJobId;
        }

        const match = this.similarJobs?.findIndex(
          (job) => Number(job.id) === Number(similarJobsId)
            || Number(job.tapwage_id) === Number(similarJobsId),
        );

        this.page = match !== -1 ? match + 1 : 1;
        this.revision = this.similarJobs;
      } else if (this.isDatapeopleAnywhereEditor) {
        this.revision = this.datapeopleAnywherePreviousRevision;
      } else {
        if (
          this.$route.query?.view === 'chrome'
          && this.$route.query?.sideRevision === 'chromePrefilled'
        ) {
          const allExtensionsIds = [
            'nopdochpomnmjnofkbheocbkconnlhfk',
            'hhnemhalmbhdldblamkefkjnalpmadhm',
            'bbkklnglldbkcleibooadedgoodihbon',
          ];

          allExtensionsIds.forEach((extensionId) => {
            window.chrome.runtime.sendMessage(
              extensionId,
              'getPrefilledContent',
              (response) => {
                if (response?.descriptions?.fields?.length) {
                  this.revision = {
                    chromePrefilled: true,
                    title: response.title,
                    description_json: response.descriptions,
                  };
                }
              },
            );
          });

          return;
        }
        /* load data for apply template view */
        this.revision = await vueGet({
          revision: this.$route?.query?.sideRevision,
          id: this.jobId,
        });
      }
    } catch (e) {
      if (e.response.code === 'access_denied') {
        this.addErrorSnackbar({ text: 'You do not have permission to view this job' });
        return;
      }
      if (e.response.code === 'job_not_found') {
        this.addErrorSnackbar({ text: 'Revision not found' });
        return;
      }
      this.addErrorSnackbar({ text: `Load error: ${e.message}` });
      throw e;
    } finally {
      this.setLoading({ componentLoading: false });
      useTracking().sideBySidePage({
        id: this.jobId,
        revisionId: this.revision?.id,
        type: this.sideBySideView,
      });
    }
  },
  methods: {
    ...mapActions(useEditor, ['setSimilarJobs']), // TODO: remove after new similar jobs implementation is complete
    ...mapActions(useAppSnackbar, ['addErrorSnackbar', 'addInfoSnackbar']),
    ...mapActions(useTracking, ['sideBySideCopyKeyboard']),
    ...mapActions(useAppLoading, ['setLoading']),
    ...mapActions(useDatapeopleAnywhereEditor, {
      datapeopleAnywhereSetSimilarJobId: 'setSimilarJobId',
    }),

    handleNextPage() {
      this.page = this.page >= this.totalPages ? this.totalPages : this.page += 1;

      // eslint-disable-next-line camelcase
      const { id, tapwage_id: tapwageId } = this.similarJobs[this.pageIndex];
      if (this.isDatapeopleAnywhereEditor) {
        this.datapeopleAnywhereSetSimilarJobId(id || tapwageId);
      } else {
        this.$router.replace({
          query: {
            ...this.$route.query,
            similarJobs: Number(id) || Number(tapwageId),
          }, /* update URL only */
        }).catch(() => false);
      }
    },
    handlePrevPage() {
      this.page = this.page <= 1 ? 1 : this.page -= 1;

      // eslint-disable-next-line camelcase
      const { id, tapwage_id: tapwageId } = this.similarJobs[this.pageIndex];
      if (this.isDatapeopleAnywhereEditor) {
        this.datapeopleAnywhereSetSimilarJobId(id || tapwageId);
      } else {
        this.$router.replace({
          query: {
            ...this.$route.query,
            similarJobs: Number(id) || Number(tapwageId),
          }, /* update URL only */
        }).catch(() => false);
      }
    },
  },
};
</script>
<style scoped lang="less">
.side-by-side-container {
  font-weight: 300;
  font-size: 16px;
}
.similar-jobs-header {
  font-size: 12px;
  font-weight: 700;
  align-items: center;
}
</style>
